import React from "react";
import PropTypes from "prop-types";
import BaseBlock from "./BaseBlock";
import useBlockData from "./useBlockData";
import { SparkLine } from "../../../components/chart-components/SparkLine.jsx";

// KPI_LABELS - human labels for the fixed, documented measure keys BuildKPIStrip emits, in
// the same order the backend returns them (internal/boardfigures/kpi.go: month_spend,
// vs_budget, mom, forecast_90d). Falls back to the raw key for any future measure so an
// unrecognized key still renders instead of showing nothing.
const KPI_LABELS = {
  month_spend: "Month spend",
  vs_budget: "vs. Budget",
  mom: "Month-over-month",
  forecast_90d: "90-day forecast",
};

// TONE_CLASS - maps the backend's neutral/adverse/favorable vocabulary
// (boardfigures.KPIMeasure.Tone, kpi.go's varianceTone) to a colour class: adverse = red
// (running over/up), favorable = green (running under/down), neutral = default ink.
const TONE_CLASS = {
  adverse: "a4-kpi-tone-adverse",
  favorable: "a4-kpi-tone-favorable",
  neutral: "a4-kpi-tone-neutral",
};

/**
 * KPIStripBlock - the 4-tile headline KPI strip (kpi_strip block type).
 *
 * GET /api/v1/board-reports/{boardReportId}/runs/{runId}/figures/kpis -> envelope key
 * "kpis" -> boardfigures.KPIStrip { measures: [KPIMeasure] }, each measure carrying a
 * pre-formatted display string, a delta/context line, a tone, and a spark series - all
 * computed server-side (see kpi.go) so this block only formats layout, not numbers.
 */
const KPIStripBlock = ({ title = "Key metrics", boardReportId, runId }) => {
  const { data, isLoading, error } = useBlockData(
    `/api/v1/board-reports/${boardReportId}/runs/${runId}/figures/kpis`
  );

  const measures = data?.kpis?.measures ?? [];

  return (
    <BaseBlock title={title} isLoading={isLoading} error={error} className="a4-kpi-strip">
      {measures.length === 0 ? (
        <p className="a4-kpi-strip-empty">No KPI data available.</p>
      ) : (
        <div className="a4-kpi-tiles">
          {measures.map((measure) => (
            <div key={measure.key} className="a4-kpi-tile">
              <div className="a4-kpi-tile-label">{KPI_LABELS[measure.key] || measure.key}</div>
              <div
                className={`a4-kpi-tile-value ${TONE_CLASS[measure.tone] || TONE_CLASS.neutral}`}
              >
                {measure.display}
              </div>
              {measure.delta && <div className="a4-kpi-tile-delta">{measure.delta}</div>}
              {Array.isArray(measure.spark) && measure.spark.length > 0 && (
                <div className="a4-kpi-tile-spark">
                  <SparkLine data={measure.spark} width={90} height={22} />
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </BaseBlock>
  );
};

KPIStripBlock.propTypes = {
  title: PropTypes.string,
  boardReportId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  runId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

export default KPIStripBlock;
